<script lang="ts" setup>
import AGrowthHack from '~/components/reusable/AGrowthHack.vue'
import YourWebHomepage from '~/components/reusable/YourWebHomepage.vue'

const { t } = useI18n()

const {
  public: { siteUrl },
} = useRuntimeConfig()

useSeoMeta({
  title: t('belong-pass-for-founders'),
  description: t('get-access-to-a-community-of'),
  ogTitle: t('belong-pass-for-founders'),
  ogDescription: t('get-access-to-a-community-of'),
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: t('belong-pass-for-founders'),
  twitterDescription: t('get-access-to-a-community-of'),
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  layout: 'menu',
})
</script>

<template>
  <div class="body text-white">
    <section class="relative">
      <div class="xl:h-174 h-auto">
        <div class="xl:pl-26">
          <h1 class="text-center xl:text-left text-[#CB99FF] pt-159 xl:pt-51">
            {{ t('belong-founder-pass') }}
          </h1>
          <h2
            class="px-5.5 text-center xl:text-left text-[1.75rem] mx-auto xl:mx-0 font-normal tracking-wider mt-2.5 leading-[2.125rem] max-w-132.25 mb-0 md:px-0"
          >
            {{ t('get-access-to-a-community') }}
          </h2>
          <div
            class="flex flex-col mt-20.5 justify-center xl:flex-row xl:justify-start"
          >
            <div
              class="relative z-2 flex items-center justify-center mx-auto xl:mx-0 w-61.75 h-8.75 rounded-[1.875rem] bg-[#1B1B1B] text-[0.8125rem] tracking-wide leading-5"
            >
              {{ t('supply-remaining') }}
              <span class="ml-2.5 text-[#CB99FF]">1000 / 1000</span>
            </div>
            <NuxtLink
              to="/"
              class="mt-2 ml-0 flex items-center justify-center text-[0.8125rem] font-light leading-5 tracking-wide underline text-white xl:mt-1 xl:ml-6 xl:justify-start"
            >
              {{ t('how-can-i-get-one') }}
            </NuxtLink>
          </div>

          <div class="hidden xl:flex flex-row mt-8 gap-3.5">
            <ReusableInstallButtons />
          </div>
        </div>
        <div
          class="header-shadow w-full object-cover absolute top-0 -z-1 h-175"
        >
          <div class="w-full object-cover absolute top-0 -z-1 h-175 xl:hidden">
            <video
              autoplay
              loop
              muted
              playsinline
              class="w-full object-cover h-175"
            >
              <source src="/images/nft/video-mobile.mp4" type="video/mp4" />
            </video>
          </div>
          <div
            class="w-full h-full object-cover absolute top-0 -z-1 hidden xl:block"
          >
            <video
              autoplay
              loop
              muted
              playsinline
              class="w-full h-full object-cover"
            >
              <source src="/images/nft/video-desktop-2.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </section>
    <section>
      <div
        class="founder-block flex flex-col-reverse items-center mb-7.5 mx-5.5 xl:flex-row xl:justify-center xl:mb-0 xl:mx-0 xl:gap-43.5"
      >
        <img
          :alt="t('early-access')"
          class="w-80.5 h-68.25 mt-0 object-top z-3 object-cover relative xl:h-136.5 xl:mt-19.5"
          src="/images/founder/phone-1.webp"
        />
        <div
          class="mx-auto mt-17.5 mb-11 relative z-3 xl:mt-52.5 xl:mb-34.5 xl:flex xl:flex-col xl:mx-0"
        >
          <div
            class="text-[1.75rem] leading-[2.625rem] text-[#FFB0EE] tracking-wider max-w-135.5 flex flex-row items-center justify-center text-center sm:text-2xl xl:justify-start xl:text-left"
          >
            {{ t('early-access') }}
          </div>
          <div
            class="text-lg leading-[1.75rem] tracking-wide max-w-127 mt-9.5 text-[#CECECE] ml-0 text-center sm:text-lg xl:text-left"
          >
            {{ t('as-a-founder-nft-holder') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="flex flex-col-reverse items-center xl:flex-row xl:justify-center"
      >
        <div class="mx-5.5 mb-16 mt-0 xl:mt-44.5 xl:mr-27.5 xl:mb-31">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide text-[#7540EE] text-center sm:text-2xl xl:text-left"
          >
            <p>
              {{ t('wl-spots') }}
              <span class="text-white">{{ t('and-exclusive-content') }}</span>
            </p>
          </div>
          <div
            class="max-w-130 mt-11.5 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] text-center sm:text-lg xl:text-left"
          >
            {{ t('our-nft-holders') }}
          </div>
        </div>
        <img
          :alt="t('wl-spots') + t('and-exclusive-content')"
          class="wl-sports w-87 h-86.5 mt-4 mb-12.5 object-cover xl:mt-39.5 xl:mb-23.5"
          src="/images/founder/wl-sports.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>

    <section>
      <div
        class="founder flex flex-col items-center xl:flex-row xl:justify-center"
      >
        <img
          :alt="t('collection') + t('notifications')"
          class="collection-notifications w-92.5 h-110.75 object-cover mt-4 mb-0 xl:my-19.5"
          src="/images/founder/premium-notifications.webp"
        />
        <div class="mx-5.5 mb-10.5 mt-4 xl:my-44.5 xl:mb-51.5 xl:ml-55">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide text-center sm:text-2xl xl:text-left"
          >
            {{ t('collection') }}
            <span class="text-[#FFC658]">{{ t('notifications') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] text-center sm:text-lg xl:text-left"
          >
            {{ t('get-hub-notifications') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>

    <section>
      <div
        class="flex flex-col-reverse items-center xl:flex-row xl:justify-center"
      >
        <div class="mx-5.5 mb-16 xl:mr-42">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide text-center sm:text-2xl xl:text-left"
          >
            <span class="text-[#8660C2]">{{ t('voting') }} </span>&nbsp;{{
              t('rights')
            }}
          </div>
          <div
            class="max-w-130.25 mt-11.5 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] text-center sm:text-lg xl:text-left"
          >
            {{ t('as-a-founder-nft') }}
          </div>
        </div>
        <img
          class="w-81.5 h-80 object-bottom my-5.5 object-cover xl:h-138"
          :alt="t('voting-rights')"
          src="/images/founder/voting-rights.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="founder flex flex-col items-center xl:flex-row xl:justify-center"
      >
        <NuxtImg
          loading="lazy"
          :alt="t('exclusive-events')"
          class="exclusive-events w-93 h-107.5 object-cover mt-4 mb-0 xl:mt-18.5 xl:mb-23"
          src="/images/founder/exclusive-events.webp"
        />
        <div class="mx-5.5 mb-10.5 mt-4 xl:ml-47.5">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide text-center sm:text-2xl xl:text-left"
          >
            <span class="text-[#FFB0F0]">{{ t('exclusive-events') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] text-center sm:text-lg xl:text-left"
          >
            {{ t('belong-founder-pass-holders') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>

    <section>
      <div
        class="founder-block flex flex-col-reverse items-center mb-7.5 mx-5.5 xl:flex-row xl:justify-center xl:mb-0 xl:mx-0"
      >
        <img
          :alt="t('special') + t('recognition')"
          class="hidden xl:block w-139.5 h-140.5 mt-5 z-1 object-cover"
          src="/images/nft/phone-1.webp"
        />
        <img
          src="/images/nft/phone-1-mobile.webp"
          :alt="t('special') + t('recognition')"
          class="block xl:hidden max-w-full w-full h-full mt-5 z-1 sm:max-w-103.5 sm:w-103.5 sm:h-54 object-cover"
        />
        <div class="mt-6 xl:mt-45">
          <div
            class="text-2xl leading-[2.625rem] tracking-wider max-w-135.5 flex flex-row items-center flex-wrap justify-center text-center sm:text-2xl xl:justify-start xl:text-left"
          >
            <img
              :alt="t('special') + t('recognition')"
              src="~/assets/images/founder-badge.svg"
              class="mr-3 w-9.5 h-10.75"
            />
            {{ t('special') }}&nbsp;<span class="text-[#8660C2]">{{
              t('recognition')
            }}</span>
          </div>
          <div
            class="text-lg leading-[1.75rem] tracking-wide max-w-115 mt-9.5 text-[#CECECE] ml-0 text-center sm:text-lg xl:ml-12.5 xl:text-left"
          >
            {{ t('nft-holders-get-special') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>

    <section>
      <div
        class="community flex flex-col-reverse items-center xl:flex-row xl:justify-center"
      >
        <div class="mx-5.5 mb-18 mt-4 xl:mt-44 xl:mr-32.5 xl:mb-58.5">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide text-center sm:text-2xl xl:text-left"
          >
            <span class="text-[#A1A1A1]">{{ t('invisibility') }} </span
            >{{ t('mode') }}
          </div>
          <div
            class="max-w-115 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] text-center sm:text-lg xl:text-left"
          >
            {{ t('stay-invisible-in-hubs') }}
          </div>
        </div>
        <img
          :alt="t('invisibility') + t('mode')"
          class="w-105.5 h-full px-3.5 mt-4 mb-0 xl:h-60.5 xl:mt-40.5 xl:mb-48 xl:px-0"
          src="/images/founder/invisibility-mode.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div class="flex flex-col items-center xl:flex-row xl:justify-center">
        <div class="mt-4 mb-0 xl:my-19.5">
          <img
            :alt="t('premium') + t('notifications')"
            class="premium-notifications w-92.5 h-74 object-cover object-top mt-0 mb-0 xl:h-110.75 xl:mt-4 xl:mb-9"
            src="/images/founder/premium-notifications.webp"
          />
        </div>
        <div class="mx-auto mb-10.5 mt-4 xl:mt-0 xl:ml-36 xl:mx-0">
          <div
            class="flex flex-row items-center text-2xl leading-[2.625rem] tracking-wide text-center justify-center px-3.5 sm:px-0 sm:flex-wrap xl:text-left xl:justify-start"
          >
            <img
              :alt="t('premium') + t('notifications')"
              src="~/assets/images/icon-premium.svg"
              class="w-6 h-3.25"
            />
            <span class="text-[#B18FFF] ml-3">{{ t('premium') }} </span>&nbsp;{{
              t('notifications')
            }}
          </div>
          <div
            class="max-w-115 mt-10 text-lg leading-[1.75rem] tracking-wide text-[#CECECE] text-center mx-3.5 xl:ml-9 xl:text-left xl:mx-0"
          >
            {{ t('subscribe-to-mint-notifications') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <YourWebHomepage />
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <AGrowthHack />
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <Supported class="pt-8 xl:pt-32.5 pb-17" />
    </section>
    <section>
      <InstallSection class="px-5" />
    </section>
  </div>
</template>

<style scoped>
@reference "tailwindcss";
.body {
  font-family: 'Poppins', sans-serif;
}

.header-shadow {
  &:after {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 118px;
  }
}

.founder-block {
  background: url(~/assets/images/creator/founder-bg.webp);
  background-position: center;
  background-size: cover;
  position: relative;
  &:before {
    background: linear-gradient(180deg, #151515 0%, rgba(21, 21, 21, 0) 100%);
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 172px;
    z-index: 0;
  }
  &:after {
    background: linear-gradient(
      180deg,
      rgba(21, 21, 21, 0) -17.46%,
      #151515 100%
    );
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 185px;
    z-index: 2;
  }
}

.nft-passes {
  position: relative;
  &:before {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 167px;
    z-index: 2;
  }
}

.gradient {
  background: linear-gradient(360deg, #0069ec 0%, #00b6f6 100%);
}

.phone-5-mobile {
  &:after {
    background: linear-gradient(180deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    height: 120px;
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
  }
}

.hub-card {
  &:nth-child(1) {
    margin-top: 0;
  }
  margin-top: -110px;
}

.community {
  position: relative;
  &:after {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 167px;
  }
}

.soulbound {
  position: relative;
  &:before {
    background: linear-gradient(0, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 167px;
    z-index: 1;
  }
}

.founder {
  position: relative;
  &:after {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 167px;
  }
}
.secondary-royalties {
  background: url(~/assets/images/creator/secondary-royalties-bg.webp) no-repeat;
  background-position: center;
  background-size: cover;
  position: relative;
  z-index: 2;
  &::before {
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 495px;
    z-index: -1;
  }
}

.web3 {
  &::before {
    background: linear-gradient(180deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
  }
  &::after {
    background: linear-gradient(0deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
    left: 0;
    right: 0;
  }
}

.member-profiles {
  &::after {
    background: linear-gradient(0deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 120px;
    z-index: 2;
    left: 0;
    right: 0;
  }
}

.phone-7 {
  position: relative;
  &:before {
    background: linear-gradient(360deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
    height: 120px;
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    z-index: 2;
  }
}

.audited-text {
  @apply absolute left-0 right-0 flex flex-col items-center max-w-163.5 h-64.75 mx-auto px-3.5 top-auto bottom-19.5 xl:top-112.5 xl:px-0 xl:bottom-auto;
}

@media (max-width: 1279.9px) {
  .nft-passes:before {
    display: none;
  }
  .founder-block,
  .community,
  .founder {
    background: none;
    &:before,
    &:after {
      display: none;
    }
  }
  .header-shadow {
    &:after {
      z-index: -1;
    }
  }
  .soulbound {
    &:before {
      height: 120px !important;
    }
    img {
      object-position: 0 -111px;
    }
    .soulbound-image {
      position: relative;
      &:after {
        background: linear-gradient(
          180deg,
          rgba(34, 34, 34, 0) 0%,
          #151515 100%
        );
        content: '';
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 120px;
        z-index: 1;
      }
    }
  }
}

@media (max-width: 424.9px) {
  .wl-sports,
  .collection-notifications,
  .exclusive-events,
  .premium-notifications {
    @apply w-full h-full px-3.5;
  }
}
</style>
